import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  AppDatePicker,
  AppInput,
  AppSelect,
  OutlineButton,
  PrimaryButton,
} from '../../../../components/ui';
import { EQUIPMENT_FIELD_KEYS } from '../../../../data/master/equipments';
import { lookupEquipmentTypes } from '../../../../repositories/lookup.repository';
import { updateUserEquipment } from '../../../../repositories/userEquipment.repository';

const COMPLIANCE_KEYS = new Set([
  'rcValidUntil',
  'pucValidUntil',
  'insuranceValidUntil',
  'fitnessValidUntil',
  'taxValidUntil',
]);

function toDateInput(value) {
  if (!value) return '';
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  return raw;
}

function assetToForm(asset) {
  if (!asset) {
    return {
      equipmentTypeId: '',
      manufacturer: '',
      model: '',
      variant: '',
      fuel: 'Diesel',
      color: '',
      regNo: '',
      chassisNumber: '',
      engineNumber: '',
      capacity: '',
      capacityUom: '',
      yom: '',
      nameAsPerRc: '',
      rcValidUntil: '',
      pucValidUntil: '',
      insuranceValidUntil: '',
      fitnessValidUntil: '',
      taxValidUntil: '',
    };
  }
  return {
    equipmentTypeId: asset.equipmentTypeId ? String(asset.equipmentTypeId) : '',
    manufacturer: asset.manufacturer || '',
    model: asset.model || '',
    variant: asset.variant || '',
    fuel: asset.fuel || 'Diesel',
    color: asset.color || '',
    regNo: asset.regNo || '',
    chassisNumber: asset.chassisNumber || '',
    engineNumber: asset.engineNumber || '',
    capacity: asset.capacity || '',
    capacityUom: asset.capacityUom || '',
    yom: asset.yom || '',
    nameAsPerRc: asset.nameAsPerRc || '',
    rcValidUntil: toDateInput(asset.rcValidUntil),
    pucValidUntil: toDateInput(asset.pucValidUntil),
    insuranceValidUntil: toDateInput(asset.insuranceValidUntil),
    fitnessValidUntil: toDateInput(asset.fitnessValidUntil),
    taxValidUntil: toDateInput(asset.taxValidUntil),
  };
}

function isFieldVisible(asset, key) {
  const req = asset?.fieldRequirements?.[key];
  return req !== 'hidden';
}

/**
 * Team / admin edit — all fields including reg / chassis / engine when verified.
 */
export function EquipmentEditModal({ isOpen, onClose, asset, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState(() => assetToForm(asset));
  const [errors, setErrors] = useState({});
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setForm(assetToForm(asset));
    setErrors({});
    let cancelled = false;
    (async () => {
      try {
        const items = await lookupEquipmentTypes();
        if (!cancelled) setEquipmentTypes(items || []);
      } catch (err) {
        if (!cancelled) {
          toast({
            title: err?.message || 'Failed to load equipment types',
            status: 'error',
            position: 'top-right',
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, asset, toast]);

  const equipmentOptions = useMemo(
    () =>
      equipmentTypes
        .filter((e) => e.active !== false)
        .map((e) => ({
          value: String(e.id),
          label: e.name,
          description: e.vehicleClass || e.category || '',
        })),
    [equipmentTypes],
  );

  const patch = (partial) => {
    setForm((f) => ({ ...f, ...partial }));
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(partial).forEach((k) => delete next[k]);
      return next;
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!asset?.id) return;

    const next = {};
    if (!form.equipmentTypeId) next.equipmentTypeId = 'Required';
    if (isFieldVisible(asset, 'manufacturer') && !form.manufacturer.trim()) {
      next.manufacturer = 'Required';
    }
    if (isFieldVisible(asset, 'regNo') && !form.regNo.trim()) {
      next.regNo = 'Required';
    }
    setErrors(next);
    if (Object.keys(next).length) {
      toast({ title: 'Fix highlighted fields', status: 'warning', position: 'top-right' });
      return;
    }

    setSaving(true);
    try {
      const body = {
        equipmentTypeId: Number(form.equipmentTypeId),
        manufacturer: form.manufacturer.trim() || null,
        model: form.model.trim() || null,
        variant: form.variant.trim() || null,
        fuel: form.fuel.trim() || null,
        color: form.color.trim() || null,
        regNo: form.regNo.trim().toUpperCase() || null,
        chassisNumber: form.chassisNumber.trim() || null,
        engineNumber: form.engineNumber.trim() || null,
        capacity: form.capacity.trim() || null,
        capacityUom: form.capacityUom.trim() || null,
        yom: form.yom.trim() || null,
        nameAsPerRc: form.nameAsPerRc.trim() || null,
        rcValidUntil: form.rcValidUntil || null,
        pucValidUntil: form.pucValidUntil || null,
        insuranceValidUntil: form.insuranceValidUntil || null,
        fitnessValidUntil: form.fitnessValidUntil || null,
        taxValidUntil: form.taxValidUntil || null,
      };

      const updated = await updateUserEquipment(asset.id, body);
      toast({
        title: 'Equipment updated',
        description: `${updated.assetId} · ${updated.equipmentType || updated.regNo}`,
        status: 'success',
        position: 'top-right',
      });
      onSaved?.(updated);
      onClose();
    } catch (err) {
      toast({
        title: err?.message || 'Failed to update equipment',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setSaving(false);
    }
  };

  if (!asset) return null;

  const basicsKeys = EQUIPMENT_FIELD_KEYS.filter(
    ({ key }) => !COMPLIANCE_KEYS.has(key) && isFieldVisible(asset, key),
  );
  const complianceKeys = EQUIPMENT_FIELD_KEYS.filter(
    ({ key }) => COMPLIANCE_KEYS.has(key) && isFieldVisible(asset, key),
  );

  const fieldInput = ({ key, label }) => {
    if (COMPLIANCE_KEYS.has(key)) {
      return (
        <AppDatePicker
          key={key}
          label={label}
          value={form[key] || ''}
          onChange={(e) => patch({ [key]: e.target.value })}
        />
      );
    }
    if (key === 'yom') {
      return (
        <AppInput
          key={key}
          label={label}
          value={form[key] || ''}
          onChange={(e) =>
            patch({ yom: e.target.value.replace(/\D/g, '').slice(0, 4) })
          }
        />
      );
    }
    if (key === 'regNo') {
      return (
        <AppInput
          key={key}
          label={label}
          isRequired
          value={form.regNo}
          onChange={(e) => patch({ regNo: e.target.value })}
          isInvalid={!!errors.regNo}
          errorMessage={errors.regNo}
        />
      );
    }
    if (key === 'manufacturer') {
      return (
        <AppInput
          key={key}
          label={label}
          isRequired
          value={form.manufacturer}
          onChange={(e) => patch({ manufacturer: e.target.value })}
          isInvalid={!!errors.manufacturer}
          errorMessage={errors.manufacturer}
        />
      );
    }
    return (
      <AppInput
        key={key}
        label={label}
        value={form[key] || ''}
        onChange={(e) => patch({ [key]: e.target.value })}
      />
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="4xl"
      isCentered
      scrollBehavior="inside"
      trapFocus={false}
    >
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        as="form"
        onSubmit={handleSave}
        bg="bg.surface"
        borderRadius="md"
        mx={4}
        my={4}
        maxH="90vh"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <ModalHeader flexShrink={0}>
          Edit equipment · {asset.assetId}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody flex="1" overflowY="auto" pb={4}>
          <VStack align="stretch" spacing={5}>
            <Box>
              <Heading size="xs" mb={3} color="text.secondary">
                Vehicle basics
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <AppSelect
                  label="Equipment type"
                  isRequired
                  isSearchable
                  value={form.equipmentTypeId}
                  onChange={(v) => patch({ equipmentTypeId: v || '' })}
                  options={equipmentOptions}
                  isInvalid={!!errors.equipmentTypeId}
                  errorMessage={errors.equipmentTypeId}
                />
                {basicsKeys
                  .filter(({ key }) => key === 'regNo')
                  .map(fieldInput)}
                {basicsKeys
                  .filter(({ key }) => key !== 'regNo')
                  .map(fieldInput)}
              </SimpleGrid>
            </Box>

            <Box>
              <Heading size="xs" mb={3} color="text.secondary">
                Compliance dates
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                {complianceKeys.map(fieldInput)}
              </SimpleGrid>
            </Box>

            <Text fontSize="13px" color="text.secondary">
              Team can edit all fields including registration, chassis, and engine
              numbers. KYC status is unchanged here — use Update status for
              verification.
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter gap={2} borderTopWidth="1px" borderColor="border.subtle">
          <OutlineButton type="button" onClick={onClose}>
            Cancel
          </OutlineButton>
          <PrimaryButton type="submit" isLoading={saving}>
            Save changes
          </PrimaryButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
